import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './Login';
import AppSelector from './AppSelector';
import { supabase } from './lib/supabase';
import ResetPassword from './ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { prefetchCommonData } from './lib/api';

// ==========================================
// LAZY LOADING - Load modules only when needed
// This reduces initial bundle size by ~70-80%
// ==========================================

const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const SalesInvoicingModule = lazy(() => import('./components/SalesInvoicingModule'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const CRMModule = lazy(() => import('./components/CRMModule'));
const InventoryManagement = lazy(() => import('./components/InventoryManagement'));
const InvoiceDetail = lazy(() => import('./components/InvoiceDetail'));
const CustomerDetail = lazy(() => import('./components/CustomerDetail'));
const NotFound = lazy(() => import('./components/NotFound'));
const CRMDashboard = lazy(() => import('./components/CRM/CRMDashboard'));
const CustomerDetailCRM = lazy(() => import('./components/CRM/CustomerDetailCRM'));
const SalesPipeline = lazy(() => import('./components/CRM/SalesPipeline'));
const DealManager = lazy(() => import('./components/CRM/DealManager'));
const PricingManagement = lazy(() => import('./components/PricingManagement'));

// Loading fallback component
const LoadingFallback = ({ darkMode }) => (
  <div className={`min-h-screen flex items-center justify-center ${
    darkMode ? 'bg-gray-900' : 'bg-gray-50'
  }`}>
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
      <p className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Loading module...
      </p>
    </div>
  </div>
);

function App() {
  const navigate = useNavigate();
  
  // State management
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem('darkMode') === 'true';
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(true);
  const [compactMode, setCompactMode] = useState(() => {
    try { return localStorage.getItem('compactMode') === 'true'; } catch { return false; }
  });

  // Warm the browser cache. There is no persistent backend to keep alive.
  useEffect(() => {
    prefetchCommonData();
  }, []);

  // Check URL for password reset
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      // Password reset will be handled by route
    }
  }, []);

  // Save dark mode preference
  useEffect(() => {
    try {
      localStorage.setItem('darkMode', darkMode);
    } catch (error) {
      console.error('Failed to save dark mode preference:', error);
    }
  }, [darkMode]);

  useEffect(() => {
    try { localStorage.setItem('compactMode', compactMode); } catch (error) {
      console.error('Failed to save compact mode preference:', error);
    }
    document.documentElement.classList.toggle('compact-mode', compactMode);
  }, [compactMode]);

  // Auth state management
  useEffect(() => {
    checkSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] Event:', event);
        
        if (event === 'SIGNED_IN' && session) {
          // Do not await another Supabase request inside onAuthStateChange.
          // The auth client can still hold its internal lock while this callback
          // runs, which can deadlock getSession() during application startup.
          setTimeout(async () => {
            try {
              const { data: profile, error } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', session.user.id)
                .single();

              if (error) {
                console.error('[Auth] Profile error:', error);
              }

              setCurrentUser({ ...session.user, profile: profile || null });
              setIsAuthenticated(true);
              console.log('[Auth] User authenticated:', session.user.email);
            } finally {
              setLoading(false);
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false);
          setCurrentUser(null);
          setLoading(false);
          console.log('[Auth] User signed out');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkSession = async () => {
    try {
      console.log('[Auth] Checking for existing session...');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        console.log('[Auth] Found session:', session.user.email);
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
          
        setCurrentUser({ ...session.user, profile });
        setIsAuthenticated(true);
      } else {
        console.log('[Auth] No session found');
      }
    } catch (error) {
      console.error('[Auth] Session error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (user) => {
    console.log('[Auth] Login callback received:', user.email);
    
    setIsAuthenticated(true);
    setCurrentUser(user);
    
    console.log('[Auth] State updated successfully');
    
    // Return promise so Login.js can await
    return Promise.resolve();
  };

  const handleLogout = async () => {
    console.log('[Auth] Logout initiated...');
    
    // Clear local state FIRST
    setIsAuthenticated(false);
    setCurrentUser(null);
    
    // Clear storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Sign out from Supabase (fire and forget - ignore errors)
    supabase.auth.signOut().catch(err => {
      console.log('[Auth] Supabase signOut warning (ignored):', err.message);
    });
    
    // Force full page reload to login (bypasses React Router issues)
    window.location.href = '/login';
    
    console.log('[Auth] Logout complete');
  };

  // Show loading screen while checking session
  if (loading) {
    return <LoadingFallback darkMode={darkMode} />;
  }

  return (
    <ErrorBoundary darkMode={darkMode}>
      <div className="App">
        <Suspense fallback={<LoadingFallback darkMode={darkMode} />}>
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <Login 
                    onLogin={handleLogin} 
                    darkMode={darkMode} 
                    setDarkMode={setDarkMode} 
                  />
                )
              } 
            />
            
            <Route 
              path="/reset-password" 
              element={
                <ResetPassword 
                  darkMode={darkMode} 
                  onSuccess={() => window.location.href = '/login'} 
                />
              } 
            />

            {/* Protected Routes - App Selector (Home) */}
            <Route
              path="/"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AppSelector
                    user={currentUser}
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                    compactMode={compactMode}
                    setCompactMode={setCompactMode}
                  />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes - Main Modules */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AnalyticsDashboard darkMode={darkMode} setDarkMode={setDarkMode} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sales-invoicing"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <SalesInvoicingModule darkMode={darkMode} setDarkMode={setDarkMode} />
                </ProtectedRoute>
              }
            />

            {/* CRM Routes */}
            <Route
              path="/crm"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <CRMDashboard darkMode={darkMode} currentUser={currentUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/customer/:id"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <CustomerDetailCRM darkMode={darkMode} currentUser={currentUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/pipeline"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <SalesPipeline 
                    darkMode={darkMode} 
                    currentUser={currentUser} 
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/deal/new"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <DealManager 
                    darkMode={darkMode} 
                    currentUser={currentUser} 
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/crm/deal/:id/edit"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <DealManager 
                    darkMode={darkMode} 
                    currentUser={currentUser} 
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/inventory"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <InventoryManagement darkMode={darkMode} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/user-management"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <UserManagement 
                    darkMode={darkMode} 
                    currentUser={currentUser} 
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pricing-management"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <PricingManagement darkMode={darkMode} currentUser={currentUser} />
                </ProtectedRoute>
              }
            />

            <Route
              path="/invoice/:invoiceNumber"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <InvoiceDetail darkMode={darkMode} />
                </ProtectedRoute>
              }
            />

            {/* Catch-all Route - 404 */}
            <Route path="*" element={<NotFound darkMode={darkMode} />} />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App;
